import { ATTR_NAMES } from '../../types/attrNames';
import { getPaymentBadgeSources } from '../../utils/paymentBadgeIcon';
import {
  offerUidsOf,
  providerUidOf,
  tagsOf,
  type FilterCategory,
  type FilterableProduct,
} from './productFilterTypes';

/**
 * The distinct values one product carries for one attribute.
 *
 * Exported because three things read an attribute's values and all three have
 * to agree: deriving a group's options, matching a selection against them, and
 * the eligibility check's citizenship test (CMS-1905). A second implementation
 * would drift from this one.
 */
export const attributeValuesOf = (
  product: FilterableProduct,
  attributeName: string | undefined,
): string[] => {
  if (!attributeName) return [];
  const attribute = product.attributes?.find(
    (a) => a?.name?.name === attributeName,
  );
  const raw = attribute?.value ?? attribute?.value_start;
  if (raw == null) return [];
  // A multi-value attribute arrives as a list or as a delimited string; a
  // boolean carries no value to offer as an option.
  if (Array.isArray(raw)) {
    return raw.flatMap((v) =>
      typeof v === 'string' && v.trim() ? [v.trim()] : [],
    );
  }
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

const CARD_NETWORKS: readonly { name: string; pattern: RegExp }[] = [
  { name: 'Visa', pattern: /visa/i },
  { name: 'Mastercard', pattern: /master\s*card|萬事達/i },
  { name: 'American Express', pattern: /american\s*express|amex|美國運通/i },
  { name: 'UnionPay', pattern: /union\s*pay|銀聯/i },
  { name: 'JCB', pattern: /jcb/i },
];

/**
 * Maps any value naming a network to its English BRD label ("VISA 白金卡" →
 * Visa, "銀聯鑽石卡" → UnionPay) so one network is one option (CMS-2070);
 * anything else passes through.
 */
const normaliseCardNetwork = (value: string): string =>
  CARD_NETWORKS.find((network) => network.pattern.test(value))?.name ?? value;

/**
 * Distinct values a product contributes to a derived group.
 *
 * Exported because matching has to agree with derivation: an option derived
 * from an attribute's values is matched against those same values, and a
 * second implementation would drift from this one.
 *
 * For a product-attribute group the values are the attribute's own values, not
 * the attribute name: `LOAN_TYPE` yields "Balance Transfer", "Fast Cash" and so
 * on, and a product carrying several reports all of them (R4.1).
 */
export const contributionOf = (
  product: FilterableProduct,
  category: FilterCategory,
): string[] => {
  switch (category.autoGenerateFrom) {
    case 'PROVIDERS': {
      const uid = providerUidOf(product);
      return uid ? [uid] : [];
    }
    case 'OFFERS':
      return offerUidsOf(product);
    case 'PRODUCT_TAGS':
      return tagsOf(product);
    case 'PRODUCT_ATTRIBUTES': {
      const name = category.valuesSourceAttribute;
      // Normalised here, so an option and the products it matches agree. Read
      // through the badge sources, so the filter and the hero badges share data.
      if (name === ATTR_NAMES.SUPPORTED_PAYMENT_NETWORK) {
        const attribute = product.attributes?.find(
          (a) => a?.name?.name === name,
        );
        return getPaymentBadgeSources(attribute ?? undefined).map((source) =>
          normaliseCardNetwork(source.code ?? source.label),
        );
      }
      return attributeValuesOf(product, name);
    }
    default:
      return [];
  }
};
